import { useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { BarChart3, BellRing, Bot, Contact, FolderKanban, Inbox, Paperclip, Settings } from 'lucide-react';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useRing } from '../../lib/ring';
import { useInboxUnread } from '../../hooks/useMailboxes';
import { BrandMark } from '../ui/BrandMark';
import { useCurrentWorkspace } from '../../hooks/useWorkspace';
import { useProjects } from '../../hooks/useProjects';
import { spring } from '../ui/motion';

const MAIL_PATHS = /^\/(inbox|needs-reply|flagged|drafts|sent|folder)(\/|$)/;

function DockItem({ to, icon, label, count, tone = 'primary', pulse, match, expanded }: { to: string; icon: ReactNode; label: string; count?: number; tone?: 'primary' | 'danger'; pulse?: boolean; match?: RegExp; expanded: boolean }) {
  const { pathname } = useLocation();
  const active = match ? match.test(pathname) : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <NavLink to={to} aria-current={active ? 'page' : undefined} aria-label={count ? `${label}, ${count}` : label} className="mp-dock-item">
      {active && <motion.span layoutId="dock-active" transition={spring} className="mp-dock-active" />}
      <span className={clsx('relative flex h-11 w-11 shrink-0 items-center justify-center', pulse && 'rounded-full ring-pulse')}>
        {icon}
        {!!count && !expanded && <span className={clsx('mp-dock-badge', tone === 'primary' && 'is-primary')}>{count > 99 ? '99+' : count}</span>}
      </span>
      <span className={clsx('relative truncate pr-2 text-[0.8125rem] font-medium transition-opacity duration-150', expanded ? 'opacity-100' : 'opacity-0')}>{label}</span>
      {!!count && expanded && <span className={clsx('mp-dock-badge is-inline', tone === 'primary' && 'is-primary')}>{count > 99 ? '99+' : count}</span>}
    </NavLink>
  );
}

/** The left icon rail: sections of the app. Mail views, accounts and categories live inside the mail stage. */
export function Dock() {
  const ring = useRing();
  const inboxUnread = useInboxUnread();
  const review = useQuery({ queryKey: qk.review('unreviewed'), queryFn: () => api('GET /api/review', { query: { status: 'unreviewed' } }), refetchInterval: 120_000 });
  const reviewCount = review.data?.items.length ?? 0;
  const i = 'h-[20px] w-[20px]';
  const { isOrg } = useCurrentWorkspace();
  const projectsNew = (useProjects(isOrg).data?.projects ?? []).reduce((n, p) => n + p.unreadCount, 0);
  // Hover (or keyboard focus) slides the rail out over the page to show labels; short delays avoid flicker.
  const [expanded, setExpanded] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setLater = (v: boolean, ms: number) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setExpanded(v), ms);
  };
  return (
    <div className="relative z-40 w-[72px] shrink-0">
      <nav
        aria-label="Sections"
        onMouseEnter={() => setLater(true, 140)}
        onMouseLeave={() => setLater(false, 180)}
        onFocus={() => setLater(true, 0)}
        onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setLater(false, 0)}
        className={clsx(
          'mp-sidebar absolute inset-y-0 left-0 flex flex-col overflow-hidden border-r px-[14px] py-3 transition-[width,box-shadow] duration-200 ease-out',
          expanded ? 'w-[232px] shadow-[8px_0_32px_-12px_rgba(15,23,42,0.35)]' : 'w-[72px]',
        )}
      >
        <NavLink to="/inbox" aria-label="MailPilot home" className="mb-3 flex h-11 items-center gap-3 rounded-[14px]">
          <BrandMark size={44} className="transition-transform hover:scale-105" />
          <span className={clsx('truncate text-base font-semibold transition-opacity duration-150', expanded ? 'opacity-100' : 'opacity-0')} style={{ color: 'var(--nav-ink)' }}>
            MailPilot
          </span>
        </NavLink>
        <div className="flex flex-1 flex-col gap-1.5">
          <DockItem expanded={expanded} to="/inbox" match={MAIL_PATHS} icon={<Inbox className={i} />} label="Mail" count={inboxUnread} />
          <DockItem expanded={expanded} to="/review" icon={<Bot className={i} />} label="AI Review" count={reviewCount} />
          <DockItem expanded={expanded} to="/reminders" icon={<BellRing className={i} />} label="Reminders" count={ring.pendingCount} tone="danger" pulse={ring.ringingNow} />
          {isOrg && <DockItem expanded={expanded} to="/projects" icon={<FolderKanban className={i} />} label="Projects" count={projectsNew} />}
          <span className="mx-1.5 my-1 h-px" style={{ background: 'var(--nav-border)' }} aria-hidden />
          <DockItem expanded={expanded} to="/contacts" icon={<Contact className={i} />} label="Contacts" />
          <DockItem expanded={expanded} to="/library" icon={<Paperclip className={i} />} label="Attachment library" />
          <DockItem expanded={expanded} to="/insights" icon={<BarChart3 className={i} />} label="Insights & Ask" />
        </div>
        <DockItem expanded={expanded} to="/settings" icon={<Settings className={i} />} label="Settings" />
      </nav>
    </div>
  );
}
